/**
 * iOS Safari WebSocket Fallback
 * iOS Safari has strict security requirements that can block WebSocket connections.
 * This module provides graceful fallback when WebSockets are unavailable.
 */

// Detect iOS Safari
export const isIOSSafari = (): boolean => {
  if (typeof window === 'undefined') return false;
  
  const userAgent = window.navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(userAgent);
  const isSafari = /Safari/.test(userAgent) && !/Chrome|CriOS/.test(userAgent);
  const isStandalone = (window.navigator as any).standalone;
  
  return (isIOS && isSafari) || isStandalone;
};

// Check if WebSockets are available
export const isWebSocketAvailable = (): boolean => {
  if (typeof window === 'undefined') return false;
  
  try {
    return 'WebSocket' in window || 'MozWebSocket' in window;
  } catch {
    return false;
  }
};

// Check secure context (required for WebSockets)
export const isSecureContext = (): boolean => {
  if (typeof window === 'undefined') return false;
  
  return window.isSecureContext || window.location.protocol === 'https:' || window.location.hostname === 'localhost';
};

/**
 * Create a realtime channel with iOS Safari fallback
 * If WebSocket fails, returns a no-op channel that doesn't crash the app
 */
export const createRealtimeChannel = (
  supabase: any,
  channelName: string,
  table: string,
  callback: () => void,
  options: {
    event?: '*' | 'INSERT' | 'UPDATE' | 'DELETE';
    schema?: string;
    filter?: string;
  } = {}
) => {
  const {
    event = '*',
    schema = 'public',
    filter
  } = options;
  
  // Check if we can use WebSockets
  const canUseWebSocket = isWebSocketAvailable() && isSecureContext();
  
  if (!canUseWebSocket) {
    console.warn('⚠️ WebSocket unavailable (iOS Safari). Real-time updates disabled.');
    
    // Return no-op unsubscribe function
    return () => {
      // Nothing to clean up
    };
  }
  
  try {
    // Try to create the channel
    let channel = supabase.channel(channelName);
    
    // Add the postgres_changes listener
    const filterObj: any = { event, schema, table };
    if (filter) {
      filterObj.filter = filter;
    }
    
    channel = channel.on('postgres_changes', filterObj, callback);
    
    // Subscribe with status callback
    channel.subscribe((status: string) => {
      if (status === 'SUBSCRIBED') {
        console.log(`✅ Realtime channel "${channelName}" connected`);
      } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
        console.warn(`⚠️ Realtime channel "${channelName}" error: ${status}. Falling back to polling.`);
      }
    });
    
    // Return unsubscribe function
    return () => {
      supabase.removeChannel(channel);
    };
  } catch (error) {
    console.error('❌ Failed to create realtime channel:', error);
    
    // Return no-op on failure
    return () => {
      // Nothing to clean up
    };
  }
};

/**
 * Safe subscribe helper for all realtime operations
 * Automatically handles iOS Safari compatibility
 */
export const safeSubscribe = (
  supabase: any,
  channelName: string,
  table: string,
  callback: () => void,
  options: {
    event?: '*' | 'INSERT' | 'UPDATE' | 'DELETE';
    schema?: string;
    filter?: string;
    onError?: (error: Error) => void;
  } = {}
): (() => void) => {
  try {
    return createRealtimeChannel(supabase, channelName, table, callback, options);
  } catch (error) {
    console.error(`Realtime subscription failed for "${channelName}":`, error);
    
    if (options.onError) {
      options.onError(error as Error);
    }
    
    // Return no-op
    return () => {};
  }
};

/**
 * Polling fallback for iOS Safari when WebSockets are unavailable
 * Polls the database at regular intervals
 */
export const createPollingFallback = (
  fetchFn: () => Promise<void>,
  intervalMs: number = 5000
): (() => void) => {
  console.warn('⚠️ Using polling fallback for real-time updates');
  
  // Initial fetch
  fetchFn();
  
  // Set up interval
  const intervalId = setInterval(fetchFn, intervalMs);
  
  // Return cleanup function
  return () => {
    clearInterval(intervalId);
  };
};

/**
 * Enhanced realtime subscription with automatic fallback
 */
export class SafeRealtimeSubscription {
  private unsubscribeFn: (() => void) | null = null;
  private isPolling = false;
  
  async subscribe(
    supabase: any,
    channelName: string,
    table: string,
    callback: () => void,
    fetchFn: () => Promise<void>,
    options: {
      event?: '*' | 'INSERT' | 'UPDATE' | 'DELETE';
      schema?: string;
      filter?: string;
      pollingInterval?: number;
    } = {}
  ): Promise<void> {
    const {
      event = '*',
      schema = 'public',
      pollingInterval = 5000
    } = options;
    
    // Try WebSocket first
    const canUseWebSocket = isWebSocketAvailable() && isSecureContext();
    
    if (canUseWebSocket) {
      this.unsubscribeFn = safeSubscribe(
        supabase,
        channelName,
        table,
        callback,
        {
          event,
          schema,
          onError: () => {
            // If WebSocket fails, fallback to polling
            this.enablePolling(fetchFn, pollingInterval);
          }
        }
      );
    } else {
      // Start polling immediately
      this.enablePolling(fetchFn, pollingInterval);
    }
  }
  
  private enablePolling(fetchFn: () => Promise<void>, intervalMs: number): void {
    this.isPolling = true;
    this.unsubscribeFn = createPollingFallback(fetchFn, intervalMs);
  }
  
  unsubscribe(): void {
    if (this.unsubscribeFn) {
      this.unsubscribeFn();
      this.unsubscribeFn = null;
    }
    this.isPolling = false;
  }
  
  get isUsingPolling(): boolean {
    return this.isPolling;
  }
}